'use client';

import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { useAuth } from '../../context/AuthContext';
import baseAPIService from '../../services/baseapiservice';

// Comprehensive user profile interface
export interface UserProfile {
  // Basic Info
  id: string;
  name: string;
  username: string;
  email: string;
  phone: string;
  avatar: string;
  type: string;
  active: boolean;

  // Personal Information
  dob?: string;
  gender?: string;
  maritalStatus?: string;
  fatherSpouseName?: string;
  bloodGroup?: string;
  religion?: string;
  category?: string;
  community?: string;
  nativePlace?: string;
  motherTongue?: string;
  languagesKnown?: string;
  physicallyHandicapped?: boolean;
  identificationMark?: string;

  // Employment Information
  employeeId?: number;
  employeeCode?: string;
  employeeType?: string;
  employeeGroup?: string;
  employeeStatus?: string;
  dateOfAppointment?: string;
  recruitmentType?: string;
  retirementAge?: number;

  // Primary Assignment
  primaryAssignment?: {
    id: number;
    departmentId: number;
    departmentName: string;
    designationId: number;
    designationName: string;
    positionId: number;
    positionName: string;
    fromDate: string;
    toDate: string;
    isPrimary: boolean;
    gradeName?: string;
    functionName?: string;
    fundName?: string;
  };

  // All Assignments
  assignments?: any[];

  // Jurisdiction/Hierarchy
  jurisdictions?: {
    boundaryTypeId: number;
    boundaryTypeName: string;
    boundaryId: number;
    boundaryName: string;
  }[];

  // Primary Boundary
  primaryBoundary?: {
    ulb?: {
      id: number;
      name: string;
    };
    boundaryType?: string;
    boundaryName?: string;
  };

  // Address Information
  permanentAddress?: string;
  permanentCity?: string;
  permanentPincode?: string;
  correspondenceAddress?: string;
  correspondenceCity?: string;
  correspondencePincode?: string;

  // Bank Details
  bankName?: string;
  branchName?: string;
  accountNumber?: string;
  ifscCode?: string;

  // Documents
  pan?: string;
  passportNumber?: string;
  gpfNumber?: string;

  // Educational Qualifications
  educationalQualifications?: {
    qualification: string;
    majorSubject: string;
    yearOfCompletion: string;
    university: string;
    document?: string;
  }[];

  // Technical Qualifications
  technicalQualifications?: {
    skills: string;
    grade: string;
    yearOfCompletion: string;
    remarks: string;
    document?: string;
  }[];

  // Department Tests
  departmentTests?: {
    testName: string;
    testCode: string;
    dateOfPassing: string;
    remarks: string;
    document?: string;
  }[];

  // Role ID (internal use only)
  roleId?: number;

  // Session Info
  lastLogin: string;
  joinDate: string;

  // Settings
  notificationSettings: {
    email: boolean;
    push: boolean;
    sms: boolean;
    inApp: boolean;
  };
  preferences: {
    theme: 'light' | 'dark';
    language: string;
    timezone: string;
  };
}

interface UserContextType {
  user: UserProfile | null;
  updateUser: (userData: Partial<UserProfile>) => void;
  saveProfile: (data: { name?: string; email?: string; mobile?: string }) => Promise<void>;
  sendMobileOtp: (mobile: string, purpose?: string) => Promise<void>;
  verifyMobileOtp: (mobile: string, otp: string) => Promise<boolean>;
  changePassword: (currentPassword: string, newPassword: string) => Promise<void>;
  refreshProfile: () => Promise<void>;
  logout: () => void;
  loading: boolean;
}

const UserContext = createContext<UserContextType | undefined>(undefined);

export const useUser = () => {
  const context = useContext(UserContext);
  if (context === undefined) {
    throw new Error('useUser must be used within a UserProvider');
  }
  return context;
};

export const UserProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { user: authUser, isAuthenticated, logout: authLogout, authType } = useAuth();
  const [user, setUser] = useState<UserProfile | null>(null);
  const [loading, setLoading] = useState(true);

  // Define fetchUserProfile with useCallback so it always has the latest authType
  const fetchUserProfile = useCallback(async () => {
    try {
      // Use apufidc-auth for APUFIDC users (fetches from apufidc_enc_db schema)
      const authEndpoint = authType === 'crda' ? 'crda-auth' : authType === 'apufidc' ? 'apufidc-auth' : 'erp-auth';
      const response: any = await baseAPIService.get(`${authEndpoint}/profile`);
      if (response?.status === 200 && response?.data?.status === 200) {
        const data = response.data.data;

        setUser(prev => {
          if (!prev) return null;

          return {
            ...prev,
            // Basic Info
            username: data.username || '',
            email: data.emailid || data.email || '',
            phone: data.mobilenumber || data.phone || '',
            type: data.type || prev.type,

            // Personal Info from employee data
            dob: data.employee?.dob || '',
            gender: data.employee?.gender || '',
            maritalStatus: data.employee?.maritalstatus || '',
            fatherSpouseName: data.employee?.fathersname || '',
            bloodGroup: data.employee?.bloodgroup || '',
            religion: data.employee?.religion || '',
            category: data.employee?.category || '',
            community: data.employee?.community || '',
            nativePlace: data.employee?.nativeplace || '',
            motherTongue: data.employee?.mothertongue || '',
            languagesKnown: data.employee?.languagesknown || '',
            physicallyHandicapped: data.employee?.physicallyhandicapped || false,
            identificationMark: data.employee?.identificationmark || '',

            // Employment Info
            employeeId: data.employee?.employee_id || data.employee?.id || data.employeeId || data.employee_id,
            employeeCode: data.employee?.employee_code || data.employee?.code || '',
            employeeType: data.employee?.employeetype_name || '',
            employeeGroup: data.employee?.employeegroup_name || '',
            employeeStatus: data.employee?.employeestatus || '',
            dateOfAppointment: data.employee?.dateofappointment || '',
            recruitmentType: data.employee?.recruitmenttype || '',
            retirementAge: data.employee?.retirementage,

            // Primary Assignment
            primaryAssignment: data.primaryAssignment ? {
              id: data.primaryAssignment.id,
              departmentId: data.primaryAssignment.departmentId,
              departmentName: data.primaryAssignment.departmentName || '',
              designationId: data.primaryAssignment.designationId,
              designationName: data.primaryAssignment.designationName || '',
              positionId: data.primaryAssignment.positionId,
              positionName: data.primaryAssignment.positionName || '',
              fromDate: data.primaryAssignment.fromDate || '',
              toDate: data.primaryAssignment.toDate || '',
              isPrimary: true,
              gradeName: data.primaryAssignment.gradeName || '',
              functionName: data.primaryAssignment.functionName || '',
              fundName: data.primaryAssignment.fundName || '',
            } : undefined,

            // Assignments
            assignments: data.assignments || [],

            // Jurisdictions - handle both camelCase and snake_case from API
            jurisdictions: (data.jurisdictions || []).map((j: any) => ({
              boundaryTypeId: j.boundaryTypeId || j.boundary_type_id || j.boundarytype,
              boundaryTypeName: j.boundaryTypeName || j.boundary_type_name || j.boundarytype_name || '',
              boundaryId: j.boundaryId || j.boundary_id || j.boundary,
              boundaryName: j.boundaryName || j.boundary_name || '',
            })),

            // Primary Boundary - from API response or fallback to first tenant
            primaryBoundary: data.primaryBoundary ? {
              ulb: data.primaryBoundary.ulb ? {
                id: data.primaryBoundary.ulb.id,
                name: data.primaryBoundary.ulb.name,
              } : (data.tenants?.[0]?.name ? {
                id: data.tenants[0]?.id,
                name: data.tenants[0]?.name,
              } : undefined),
              boundaryType: data.primaryBoundary.ulb?.type || 'City',
              boundaryName: data.primaryBoundary.ulb?.name || data.primaryBoundary.regional?.name || '',
            } : prev?.primaryBoundary,

            // Address
            permanentAddress: data.employee?.permanentaddress || '',
            permanentCity: data.employee?.permanentcity || '',
            permanentPincode: data.employee?.permanentpincode || '',
            correspondenceAddress: data.employee?.correspondenceaddress || '',
            correspondenceCity: data.employee?.correspondencecity || '',
            correspondencePincode: data.employee?.correspondencepincode || '',

            // Bank Details
            bankName: data.employee?.bank_name || '',
            branchName: data.employee?.branch_name || '',
            accountNumber: data.employee?.accountnumber || '',
            ifscCode: data.employee?.ifsccode || '',

            // Documents
            pan: data.employee?.pan || '',
            passportNumber: data.employee?.passportnumber || '',
            gpfNumber: data.employee?.gpfnumber || '',

            // Role ID (internal)
            roleId: data.rle_id,
          };
        });
      }
    } catch (error) {
      console.error('Error fetching user profile:', error);
    }
  }, [authType]);

  useEffect(() => {
    if (isAuthenticated && authUser) {
      // Initial user data from auth context
      const initialUser: UserProfile = {
        id: String(authUser.uid || ''),
        name: authUser.name || '',
        username: '',
        email: '',
        phone: '',
        avatar: '',
        type: authUser.type || '',
        active: true,
        lastLogin: authUser.lastLoginDate || '',
        joinDate: '',
        primaryBoundary: authUser.primaryBoundary ? {
          ulb: authUser.primaryBoundary.ulb ? {
            id: authUser.primaryBoundary.ulb.id,
            name: authUser.primaryBoundary.ulb.name,
          } : undefined,
          boundaryType: authUser.primaryBoundary.ulb?.type,
          boundaryName: authUser.primaryBoundary.ulb?.name,
        } : undefined,
        notificationSettings: {
          email: true,
          push: true,
          sms: false,
          inApp: true,
        },
        preferences: {
          theme: 'light',
          language: 'en',
          timezone: 'UTC+5:30',
        },
      };

      setUser(initialUser);
      setLoading(false);

      // Fetch detailed profile from API
      fetchUserProfile();
    } else {
      setUser(null);
      setLoading(false);
    }
  }, [isAuthenticated, authUser, fetchUserProfile]);

  const refreshProfile = async () => {
    setLoading(true);
    try {
      await fetchUserProfile();
    } finally {
      setLoading(false);
    }
  };

  const updateUser = (userData: Partial<UserProfile>) => {
    if (user) {
      setUser({
        ...user,
        ...userData,
      });
    }
  };

  const changePassword = async (currentPassword: string, newPassword: string): Promise<void> => {
    try {
      const response: any = await baseAPIService.post('erp-auth/change-password', {
        userId: parseInt(user?.id || '0'),
        currentPassword,
        newPassword,
      });

      if (response?.status === 200 && response?.data?.status === 200) {
        return;
      } else {
        throw new Error(response?.data?.err_message || response?.data?.message || 'Failed to change password');
      }
    } catch (error: any) {
      console.error('Error changing password:', error);
      throw new Error(error?.response?.data?.err_message || error?.message || 'Failed to change password');
    }
  };

  // Persist editable profile fields (name / email / mobile) to the backend table.
  const saveProfile = async (data: { name?: string; email?: string; mobile?: string }): Promise<void> => {
    try {
      const authEndpoint = authType === 'crda' ? 'crda-auth' : authType === 'apufidc' ? 'apufidc-auth' : 'erp-auth';
      const response: any = await baseAPIService.put(`${authEndpoint}/profile`, {
        name: data.name,
        email: data.email,
        mobile: data.mobile,
      });

      if (response?.status === 200 && response?.data?.status === 200) {
        const updated = response.data.data || {};
        setUser(prev => prev ? {
          ...prev,
          name: updated.name ?? prev.name,
          email: updated.emailid ?? prev.email,
          phone: updated.mobilenumber ?? prev.phone,
        } : prev);
        return;
      }
      throw new Error(response?.data?.err_message || response?.data?.message || 'Failed to update profile');
    } catch (error: any) {
      console.error('Error updating profile:', error);
      throw new Error(error?.response?.data?.err_message || error?.message || 'Failed to update profile');
    }
  };

  // Send an OTP to a mobile number for verification. `purpose` selects the SMS template:
  // 'change-password' / 'password-reset' use the password-reset template; default is mobile-update.
  const sendMobileOtp = async (mobile: string, purpose?: string): Promise<void> => {
    const authEndpoint = authType === 'crda' ? 'crda-auth' : authType === 'apufidc' ? 'apufidc-auth' : 'erp-auth';
    try {
      const response: any = await baseAPIService.post(`${authEndpoint}/profile/mobile-otp/send`, { mobile, purpose });
      if (response?.status === 200 && response?.data?.status === 200) return;
      throw new Error(response?.data?.err_message || response?.data?.message || 'Failed to send OTP');
    } catch (error: any) {
      throw new Error(error?.response?.data?.err_message || error?.message || 'Failed to send OTP');
    }
  };

  // Verify the OTP entered for a new mobile number.
  const verifyMobileOtp = async (mobile: string, otp: string): Promise<boolean> => {
    const authEndpoint = authType === 'crda' ? 'crda-auth' : authType === 'apufidc' ? 'apufidc-auth' : 'erp-auth';
    try {
      const response: any = await baseAPIService.post(`${authEndpoint}/profile/mobile-otp/verify`, { mobile, otp });
      if (response?.status === 200 && response?.data?.status === 200) {
        return !!response?.data?.data?.verified;
      }
      throw new Error(response?.data?.err_message || response?.data?.message || 'OTP verification failed');
    } catch (error: any) {
      throw new Error(error?.response?.data?.err_message || error?.message || 'OTP verification failed');
    }
  };

  const logout = () => {
    setUser(null);
    authLogout();
  };

  const value = {
    user,
    updateUser,
    saveProfile,
    sendMobileOtp,
    verifyMobileOtp,
    changePassword,
    refreshProfile,
    logout,
    loading,
  };

  return (
    <UserContext.Provider value={value}>
      {children}
    </UserContext.Provider>
  );
};
